<template>
  <div class="single-filter search-filter-dropdown">
    <div class="dropdown dropdown-with-animation" :class="{'disabled': data.disabled}">
      <!-- Trigger input field -->
      <div :id="inputFieldId" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
        <input type="text"
               class="form-control cursor-pointer"
               :placeholder="data.placeholder || 'Search postcode...'"
               :disabled="data.disabled"
               @click="startNavigation"
               :value="showValue"
               @input="getSearchValue($event)" />
      </div>

      <!-- Dropdown list with search field and results -->
      <div class="dropdown-menu py-0 my-1" :class="data.listClass" :aria-labelledby="inputFieldId">
        <div class="form-group form-group-with-search">
          <span class="form-control-feedback">
            <app-icon name="search" />
          </span>
          <input type="text"
                 ref="searchInput"
                 :class="'form-control ' + data.listItemInputClass"
                 :placeholder="$t('search')"
                 v-model="searchValue"
                 @input="fetchAddresses"
                 @keydown.down="navigateDown"
                 @keydown.up="navigateUp"
                 @keydown.enter.prevent="enterSelectedValue" />
        </div>

        <div class="dropdown-divider my-0" />
        
        <!-- Search result wrapper -->
        <div class="dropdown-search-result-wrapper custom-scrollbar" ref="optionList">
          <a class="dropdown-item"
             href="#"
             v-for="(item, index) in options"
             :class="{
               'active': index === activeIndex,
               [data.listItemClass]: !isUndefined(data.listItemClass),
               'selected': item === selectedAddress,
               'disabled': item.disabled
             }"
             @click.prevent="changeSelectedValue(item, index)"
             :key="`${inputFieldId}-${index}`">
            {{ item }}
            <span class="check-sign float-right">
              <app-icon name="check" class="menu-icon" />
            </span>
          </a>

          <!-- No options found message -->
          <div v-if="!options.length" class="text-center text-muted text-size-13 py-primary">
            {{ hintText }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { InputMixin } from './mixin/InputMixin.js';
import { NavigationMixin } from './mixin/NavigationMixin';
import CoreLibrary from '../../helpers/CoreLibrary';
import debounce from 'lodash.debounce'; // Import debounce for better performance

export default {
  name: "PostCodeInput",
  mixins: [InputMixin, NavigationMixin],
  extends: CoreLibrary,
  data() {
    return {
      selectedAddress: this.value, // The selected address
      selectedAddressObj: {}, // The selected address
      searchValue: '',       // Search input value
      addresses: [],         // Fetched address suggestions
      activeIndex: -1,       // Index for keyboard navigation
      addressObjects: [],
    };
  },
  computed: {
    // Dynamically filter addresses based on search input and exclude the selected value
    options() {
      this.activeIndex = -1;
      return this.addresses.filter(item => item !== this.selectedAddress); // Remove selected address from dropdown
    },
    showValue() {
      return this.selectedAddress || '';
    },
    hintText() {
      return !this.addresses.length ? this.$t('no_options_found') : (!this.options.length ? this.$t('did_not_match_anything') : '');
    }
  },
  methods: {
    // Fetch addresses based on search input
    fetchAddresses: debounce(async function () {
      if (this.searchValue.length >= 3) {
        try {
          const response = await fetch(`https://api.postcodes.io/postcodes/${this.searchValue}/nearest`);
          if (!response.ok) throw new Error("Failed to fetch addresses");

          const data = await response.json();
          // Map through the result and format the address into a single string
          this.addresses = data.result.map(item =>
            `${item.admin_district}, ${item.region}, ${item.postcode}, ${item.country}`
          );
          this.addressObjects = data.result;
          this.activeIndex = -1;
        } catch (error) {
          console.error("Error fetching addresses:", error);
          this.addresses = [];
        }
      } else {
        this.addresses = [];
      }
    }, 300), // Debounce to reduce excessive API calls

    // Handle search input changes
    getSearchValue($event) {
      this.searchValue = $event.target.value;
      this.fetchAddresses();
    },

    // Change selected value when clicking on an option
    async changeSelectedValue(address, index) {
      this.selectedAddressObj = this.addressObjects[index];
      this.selectedAddress = this.selectedAddressObj?.postcode;
      this.searchValue = "";
      this.addresses = this.addresses.filter(item => item !== address); // Remove selected value from the list
      this.$emit('input', this.selectedAddress);
      this.$emit('postal-code-found', this.selectedAddressObj);
    },

    // Select the highlighted option when pressing enter
    enterSelectedValue() {
      if (this.activeIndex !== -1) {
        this.changeSelectedValue(this.addresses[this.activeIndex]);
      }
    },
    clear() {
      this.selectedValue = '';
      this.changed();
    }
  }
};
</script>

<style scoped>
/* Add your existing styles and customize as needed */
.active {
  background-color: #e0e0e0;
}

.dropdown-search-result-wrapper {
  max-height: 200px;
  overflow-y: auto;
}
</style>
